/*
 * The License Detail modal's Quotas tab (audit Phase 3.3). Replaces
 * wwwroot/js/License/Quota.js on the Phase 3.1 core. No jQuery.
 *
 * Four attachment depths in one tab -- License root, Domain Binder,
 * Application Binder, one Resource -- plus the resolved view underneath. Every
 * mutation reloads the whole tab rather than patching its row: an edit at one
 * depth changes the RESOLVED numbers below, sometimes for a counter the
 * operator did not touch, and a partial update would leave the resolved view
 * stating the previous answer on the one screen that exists to say what the
 * numbers come out as.
 *
 * The unlimited sentinel (long.MaxValue) never reaches this file. The server
 * sends `unlimited: true` with a null number beside it, because
 * 9223372036854775807 does not survive JSON.parse -- it rounds to a value one
 * greater and will not round-trip back. The checkbox owns the state here and
 * the server re-derives from it; the number input is disabled while it is
 * ticked, so the two can never be posted in disagreement.
 */
import { ajaxHtml, ajaxJson } from '../core/ajax';
import { one } from '../core/dom';
import { url as absoluteUrl } from '../core/http';
import { confirm } from '../core/modal';
import { toast } from '../core/toast';
import { failToast, reloadTab } from './license';

const TAB = '#LicenseTabQuotas';

/** QuotaMeasure.Rate. Period and Rolling mean nothing at any other measure. */
const MEASURE_RATE = 2;
/** QuotaOnExceed: Block is 0, Warn 1, Evict 3. Block and Evict both need a live count. */
const ON_EXCEED_BLOCK = '0';
const ON_EXCEED_WARN = '1';
const ON_EXCEED_EVICT = '3';

const UNCOUNTABLE_TITLE =
    'Nothing in this build counts this quantity at this scope, so a hard block cannot be '
    + 'honoured. The limit is still published, and the consuming application enforces it.';

/**
 * Level -> route pair. The Web controller mirrors the Api's own route shape,
 * so these read the same on both sides.
 */
const ROUTES: Record<string, { add(parentId: string): string; one(quotaId: number): string }> = {
    License: {
        add: parentId => `License/${parentId}/Quota`,
        one: quotaId => `License/Quota/${quotaId}`,
    },
    DomainBinder: {
        add: parentId => `License/DomainBinder/${parentId}/Quota`,
        one: quotaId => `License/DomainBinder/Quota/${quotaId}`,
    },
    AppBinder: {
        add: parentId => `License/AppBinder/${parentId}/Quota`,
        one: quotaId => `License/AppBinder/Quota/${quotaId}`,
    },
    Resource: {
        add: parentId => `License/AppBinder/${parentId}/ResourceQuota`,
        one: quotaId => `License/ResourceQuota/${quotaId}`,
    },
};

function routesFor(level: string) {
    return ROUTES[level] ?? ROUTES['License']!;
}

function levelCard(level: string, parentId: string): Element | null {
    return one(`.license-quota-tab .lc-quota-level[data-quota-level="${level}"][data-parent-id="${parentId}"]`);
}

function text(root: ParentNode, selector: string): string {
    return (root.querySelector(selector) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
}

function int(root: ParentNode, selector: string): number {
    return parseInt(text(root, selector), 10);
}

function checked(root: ParentNode, selector: string): boolean {
    return (root.querySelector(selector) as HTMLInputElement | null)?.checked === true;
}

/**
 * A number input is sent only when its "unlimited" / "none" checkbox is clear.
 * Sending both is what makes the pair ambiguous, and the server refuses an
 * ambiguous pair rather than guessing a direction -- guessing unlimited grants,
 * guessing 0 denies, and both are wrong.
 */
function limitPair(value: string, unlimited: boolean): { value: string | null; flag: boolean } {
    return unlimited ? { value: null, flag: true } : { value, flag: false };
}

/** The fields every quota row carries, read from one add-row or one saved row. */
function readQuota(root: ParentNode, prefix: string): Record<string, unknown> {
    const measure = int(root, `.${prefix}-measure`);
    const limit = limitPair(text(root, `.${prefix}-limit`), checked(root, `.${prefix}-unlimited`));
    const soft = limitPair(text(root, `.${prefix}-soft`), checked(root, `.${prefix}-soft-none`));
    return {
        Scope: int(root, `.${prefix}-scope`),
        Measure: measure,
        // Period and Rolling are Rate-only. The server normalises both away at
        // every other measure, and a stale one left behind by a measure change
        // would make the row look like a rate limit to anything reading the
        // column -- so they are sent as null / a hard false rather than omitted.
        Period: measure === MEASURE_RATE ? int(root, `.${prefix}-period`) : null,
        Rolling: measure === MEASURE_RATE && checked(root, `.${prefix}-rolling`),
        Limit: limit.value,
        Unlimited: limit.flag,
        SoftLimit: soft.value,
        SoftLimitUnlimited: soft.flag,
        OnExceed: int(root, `.${prefix}-onexceed`),
        IsOverride: checked(root, `.${prefix}-override`),
        Precedence: int(root, `.${prefix}-precedence`) || 0,
    };
}

export const Quota = {
    Add(level: string, parentId: string): void {
        const card = levelCard(level, parentId);
        const foot = card?.querySelector('.lc-quota-add');
        if (!foot) return;
        const counterKey = text(foot, '.lc-add-q-key').trim();
        if (!counterKey) {
            toast('danger', 'Pick a counter first');
            return;
        }
        const data: Record<string, unknown> = { CounterKey: counterKey, ...readQuota(foot, 'lc-add-q') };
        if (level === 'Resource') {
            const target = int(foot, '.lc-add-q-target');
            if (!target || target <= 0) {
                toast('danger', 'Pick a target resource first');
                return;
            }
            data['TargetResourceId'] = target;
        }
        void ajaxJson({
            url: absoluteUrl(routesFor(level).add(parentId)),
            method: 'POST',
            data,
            errorTitle: 'Failed to add quota',
            onSuccess: () => {
                toast('success', 'Quota added');
                void Quota.ReloadTab();
            },
            onFailure: failToast('Failed to add quota'),
        });
    },

    Save(level: string, quotaId: number): void {
        const row = one(`.license-quota-tab tr[data-quota-id="${quotaId}"]`);
        if (!row) return;
        const data: Record<string, unknown> = {
            Id: quotaId,
            CounterKey: text(row, '.lc-q-key'),
            ...readQuota(row, 'lc-q'),
        };
        if (level === 'Resource') data['TargetResourceId'] = int(row, '.lc-q-target');
        void ajaxJson({
            url: absoluteUrl(routesFor(level).one(quotaId)),
            method: 'PUT',
            data,
            errorTitle: 'Failed to save quota',
            onSuccess: () => {
                toast('success', 'Quota saved');
                void Quota.ReloadTab();
            },
            onFailure: failToast('Failed to save quota'),
        });
    },

    Delete(level: string, quotaId: number): void {
        // Removing a quota widens whatever it capped, back to the shallower row
        // or to whatever the counter says its silence means. Worth confirming,
        // not worth a paragraph.
        void confirm({
            title: 'Remove Quota',
            html: 'Remove this quota?<br/><br/>Whatever it capped falls back to the next level up.',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: absoluteUrl(routesFor(level).one(quotaId)),
                    method: 'DELETE',
                    errorTitle: 'Failed to remove quota',
                    onSuccess: () => {
                        toast('success', 'Quota removed');
                        void Quota.ReloadTab();
                    },
                    onFailure: failToast('Failed to remove quota'),
                });
            },
        });
    },

    ReloadTab(): Promise<void> {
        return reloadTab(TAB);
    },

    /**
     * Live counts, on demand, and it stays that way. Every other number in this
     * tab is configuration; these are live counts, which invariant 7 forbids
     * caching -- so folding them into the tab render would mean re-counting
     * every countable limit after every edit above, for an answer that is stale
     * the moment it lands either way.
     */
    LoadUsage(): void {
        const target = one('.license-quota-tab .lc-quota-usage');
        if (!target) return;
        void ajaxHtml({
            url: `License/${target.getAttribute('data-license-id')}/QuotaUsage`,
            busy: '.lc-usage-busy',
            alertTitle: 'Failed to measure quota usage',
            onSuccess: html => { target.innerHTML = html; },
        });
    },
};

// ------------------------------------------------------- the tab's own rules

/**
 * Period and Rolling are Rate-only. Hidden rather than disabled, so the row
 * does not carry controls that mean nothing at the measure it is set to. Both
 * wear .lc-rate-only rather than being matched by a class substring, so adding
 * a third window field is one class and not another selector to keep in step.
 */
function applyMeasure(select: HTMLSelectElement): void {
    const cell = select.closest('td');
    if (!cell) return;
    const isRate = parseInt(select.value, 10) === MEASURE_RATE;
    for (const node of cell.querySelectorAll('.lc-rate-only')) node.classList.toggle('d-none', !isRate);
}

/**
 * The checkbox owns the number: disabling the input while it is ticked is what
 * keeps the pair from ever being posted in disagreement, and it is also the
 * only affordance telling the operator that "unlimited" is a value rather than
 * an empty box.
 *
 * Clearing the number happens on a TICK and nowhere else. The jQuery original
 * wrote `.val(checked ? '' : '')`, which clears on both edges -- so unticking
 * the box threw away the number the operator had typed before ticking it, and
 * they had to type it again to get back where they started. Arrival never
 * clears either: the server renders the stored number beside a ticked box on
 * some rows, and wiping it on render would turn a display into an edit.
 */
function applyUnlimited(box: HTMLInputElement, clearOnTick: boolean): void {
    const cell = box.closest('td');
    if (!cell) return;
    for (const node of cell.querySelectorAll<HTMLInputElement>('input[type="number"]')) {
        node.disabled = box.checked;
        if (box.checked && clearOnTick) node.value = '';
    }
}

/**
 * Whether a hard block can be honoured depends on the counter AND the scope
 * together: mcp:person is countable per Domain and per Tenant and not per
 * Application, because nothing says which People an Application has. So this
 * re-runs on a scope change too, not only on a counter change.
 *
 * The two masks read OPPOSITE. data-scopes is AllowedScopes, where 0 means "no
 * opinion, offer every denominator"; data-countable-scopes is what this build
 * can count, where 0 means it can count NONE. Treating them the same way is how
 * Block ends up offered on every advisory counter.
 */
export function applyCounterConstraints(foot: Element): void {
    const key = foot.querySelector('.lc-add-q-key') as HTMLSelectElement | null;
    const option = key?.selectedOptions?.[0];
    if (!option) return;
    const allowed = parseInt(option.getAttribute('data-scopes') ?? '', 10) || 0;
    const countable = parseInt(option.getAttribute('data-countable-scopes') ?? '', 10) || 0;

    const scope = foot.querySelector('.lc-add-q-scope') as HTMLSelectElement | null;
    if (!scope) return;
    for (const scopeOption of scope.options) {
        const bit = 1 << parseInt(scopeOption.value, 10);
        scopeOption.disabled = allowed !== 0 && (allowed & bit) === 0;
    }
    if (scope.selectedOptions[0]?.disabled) {
        const firstEnabled = Array.from(scope.options).find(o => !o.disabled);
        if (firstEnabled) scope.value = firstEnabled.value;
    }

    const scopeBit = 1 << parseInt(scope.value, 10);
    const canBlock = (countable & scopeBit) !== 0;
    const onExceed = foot.querySelector('.lc-add-q-onexceed') as HTMLSelectElement | null;
    if (!onExceed) return;
    for (const exceedOption of onExceed.options) {
        if (exceedOption.value !== ON_EXCEED_BLOCK && exceedOption.value !== ON_EXCEED_EVICT) continue;
        exceedOption.disabled = !canBlock;
        exceedOption.title = canBlock ? '' : UNCOUNTABLE_TITLE;
    }
    if (!canBlock && (onExceed.value === ON_EXCEED_BLOCK || onExceed.value === ON_EXCEED_EVICT)) {
        onExceed.value = ON_EXCEED_WARN;
    }
}

/**
 * Wire one rendering of the Quotas tab.
 *
 * The jQuery original hung four document-level change handlers off this tab and
 * nothing else, which meant every rule ran only on a change the operator made:
 * a freshly rendered add-row offered Block and Evict on a counter that cannot
 * be counted, until the operator happened to touch the counter select. An
 * enhancer runs the same rules on arrival as well -- first paint, a tab load or
 * a reload after a write -- and the marker attribute keeps a re-enhanced
 * subtree from binding a second listener.
 */
export function enhanceQuotaTab(root: ParentNode): void {
    const tabs: Element[] = [];
    if (root instanceof Element && root.matches('.license-quota-tab')) tabs.push(root);
    tabs.push(...Array.from(root.querySelectorAll('.license-quota-tab')));
    for (const tab of tabs) {
        for (const measure of tab.querySelectorAll<HTMLSelectElement>('.lc-q-measure, .lc-add-q-measure')) applyMeasure(measure);
        for (const box of tab.querySelectorAll<HTMLInputElement>('.lc-q-unlimited, .lc-add-q-unlimited, .lc-q-soft-none, .lc-add-q-soft-none')) {
            applyUnlimited(box, false);
        }
        for (const foot of tab.querySelectorAll('.lc-quota-add')) applyCounterConstraints(foot);

        if (tab.getAttribute('data-mcp-quota-bound') === 'true') continue;
        tab.setAttribute('data-mcp-quota-bound', 'true');
        tab.addEventListener('change', event => {
            const target = event.target;
            if (target instanceof HTMLSelectElement && target.matches('.lc-q-measure, .lc-add-q-measure')) {
                applyMeasure(target);
            } else if (target instanceof HTMLInputElement
                && target.matches('.lc-q-unlimited, .lc-add-q-unlimited, .lc-q-soft-none, .lc-add-q-soft-none')) {
                applyUnlimited(target, true);
            } else if (target instanceof HTMLSelectElement && target.matches('.lc-add-q-key')) {
                const foot = target.closest('.lc-quota-add');
                if (foot) applyCounterConstraints(foot);
                // A counter states the measure it is meant to be read at; adopt
                // it, then let the measure rule re-run off the new value.
                const measure = target.selectedOptions[0]?.getAttribute('data-measure');
                const measureSelect = foot?.querySelector('.lc-add-q-measure') as HTMLSelectElement | null;
                if (measure !== null && measure !== undefined && measureSelect) {
                    measureSelect.value = measure;
                    applyMeasure(measureSelect);
                }
            } else if (target instanceof HTMLSelectElement && target.matches('.lc-add-q-scope')) {
                const foot = target.closest('.lc-quota-add');
                if (foot) applyCounterConstraints(foot);
            }
        });
    }
}
